/**
 * XY Electron SDK YUV Renderer
 *
 * @summary renderer by webgl
 *
 * Created at     : 2022-03-04 17:14:46
 * Last modified  : 2022-06-08 16:51:00
 */

import { getWebGLContext, initShaders } from "./cuonUtils";
import {
  VSHADER_SOURCE,
  FSHADER_SOURCE,
  vertices0,
  vertices90,
  vertices180,
  vertices270,
} from "./enum";

/**
 * XY Electron YUV Renderer
 */
class Render {
  public canvas: any;
  public gl: any;
  public vertexBuffer: any;
  public NumCache: any;

  /**
   * Render constructor
   *
   * @param canvas render canvas element
   */
  constructor(canvas, NumCache = {}) {
    this.canvas = canvas;
    this.gl = null;
    this.vertexBuffer = null;
    this.NumCache = NumCache;

    this.init();
  }

  init() {
    this.gl = getWebGLContext(this.canvas);
    if (!this.gl) {
      console.log("Failed to get the rendering context for WebGL");
      return;
    }

    if (!initShaders(this.gl, VSHADER_SOURCE, FSHADER_SOURCE)) {
      console.log("Failed to init shaders.");
      return;
    }

    this.initVertexBuffers();
  }

  initVertexBuffers() {
    const vertices = new Float32Array([
      1, 1, 0.0, -1, 1, 0.0, 1, -1, 0.0, -1, -1, 0.0,
    ]);

    const n = 4; // 点的个数

    // 创建缓冲区对象
    this.vertexBuffer = this.gl.createBuffer();
    if (!this.vertexBuffer) {
      console.log("Failed to create the buffer object");
      return -1;
    }

    // 将缓冲区对象绑定到目标
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.vertexBuffer);
    // 向缓冲区对象写入数据
    this.gl.bufferData(this.gl.ARRAY_BUFFER, vertices, this.gl.STATIC_DRAW);

    const a_Position = this.gl.getAttribLocation(this.gl.program, "a_Position");
    if (a_Position < 0) {
      console.log("Failed to get the storage location of a_Position");
      return -1;
    }
    // 将缓冲区对象分配给a_Position变量
    this.gl.vertexAttribPointer(a_Position, 3, this.gl.FLOAT, false, 0, 0);

    // 连接a_Position变量与分配给它的缓冲区对象
    this.gl.enableVertexAttribArray(a_Position);

    const textureRect = new Float32Array([
      1.0, 0.0, 0.0, 0.0, 1.0, 1.0, 0.0, 1.0,
    ]);
    //var textureRect = new Float32Array([0.75, 0.0, 0.25, 0.0, 0.75, 1.0, 0.25, 1.0])
    const textureBuffer = this.gl.createBuffer();
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, textureBuffer);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, textureRect, this.gl.STATIC_DRAW);
    const textureCoord = this.gl.getAttribLocation(
      this.gl.program,
      "a_texturePosition"
    );
    this.gl.vertexAttribPointer(textureCoord, 2, this.gl.FLOAT, false, 0, 0);
    this.gl.enableVertexAttribArray(textureCoord);

    this.gl.pixelStorei(this.gl.UNPACK_ALIGNMENT, 1);

    return n;
  }

  cropImage(buffer, width, height, width1, height1) {
    let wxh1 = this.NumCache[`${width1}*${height1}`];
    let bufLen = this.NumCache[`${wxh1}*3/2`];
    let selfWidth1 = this.NumCache[`${width1}/2`];
    let wxh = this.NumCache[`${width}*${height}`];

    if (!wxh1) {
      wxh1 = this.NumCache[`${width1}*${height1}`] = width1 * height1;
    }

    if (!bufLen) {
      bufLen = this.NumCache[`${wxh1}*3/2`] = (wxh1 * 3) / 2;
    }

    if (!selfWidth1) {
      selfWidth1 = this.NumCache[`${width1}/2`] = width1 / 2;
    }

    if (!wxh) {
      wxh = this.NumCache[`${width}*${height}`] = width * height;
    }

    let buffer1 = new Uint8Array(bufLen);

    const helfHeight1 = height1 / 2;

    for (let i = 0; i < height1; i++) {
      const w1 = width * i;
      const w11 = width1 * i;

      buffer1.set(buffer.subarray(w1, w1 + width1), w11);
    }

    for (let i = 0; i < helfHeight1; i++) {
      const start = wxh + (width * i) / 2;

      buffer1.set(
        buffer.subarray(start, start + selfWidth1),
        wxh1 + (width1 * i) / 2
      );
    }

    const end = (wxh1 * 5) / 4;
    const end1 = (wxh * 5) / 4;

    for (let i = 0; i < helfHeight1; i++) {
      const start = end1 + (width * i) / 2;

      buffer1.set(
        buffer.subarray(start, start + selfWidth1),
        end + (width1 * i) / 2
      );
    }

    return buffer1;
  }

  initTextureBuffer(content: any, width: any, height: any, rotation: any) {
    let buffer = new Uint8Array(content);
    let width1 = Math.floor(width);
    let height1 = Math.floor(height);

    if (width1 != width || height1 != height) {
      buffer = this.cropImage(buffer, width, height, width1, height1);
      width = width1;
      height = height1;
    }

    const canvasW = this.canvas.width;
    const canvasH = this.canvas.height;
    let rotateW = width;
    let rotateH = height;

    if (rotation == 1 || rotation == 3) {
      rotateW = height;
      rotateH = width;
    }

    let canvasWxrotateH = this.NumCache[`${canvasW}*${rotateH}`];
    let canvasHxrotateW = this.NumCache[`${canvasH}*${rotateW}`];

    if (!canvasWxrotateH) {
      this.NumCache[`${canvasW}*${rotateH}`] = canvasWxrotateH =
        canvasW * rotateH;
    }

    if (!canvasHxrotateW) {
      this.NumCache[`${canvasH}*${rotateW}`] = canvasHxrotateW =
        canvasH * rotateW;
    }

    if (canvasWxrotateH > canvasHxrotateW) {
      let displayWidth = this.NumCache[`${canvasHxrotateW}/${rotateH}`];
      let viewportY = this.NumCache[`(${canvasW}-${displayWidth})/2`];

      if (!displayWidth) {
        displayWidth = this.NumCache[`${canvasHxrotateW}/${rotateH}`] =
          Math.round(canvasHxrotateW / rotateH);
      }

      if (!viewportY) {
        viewportY = this.NumCache[`(${canvasW}-${displayWidth})/2`] =
          Math.round((canvasW - displayWidth) / 2);
      }

      this.gl.viewport(viewportY, 0, displayWidth, canvasH);
    } else {
      let displayHeight = this.NumCache[`${canvasWxrotateH}/${rotateW}`];
      let viewportY = this.NumCache[`(${canvasH}-${displayHeight})/2`];

      if (!displayHeight) {
        displayHeight = this.NumCache[`${canvasWxrotateH}/${rotateW}`] =
          Math.round(canvasWxrotateH / rotateW);
      }

      if (!viewportY) {
        viewportY = this.NumCache[`(${canvasH}-${displayHeight})/2`] =
          Math.round((canvasH - displayHeight) / 2);
      }

      this.gl.viewport(0, viewportY, canvasW, displayHeight);
    }

    var texture0 = this.gl.createTexture();
    this.gl.activeTexture(this.gl.TEXTURE0);
    this.gl.bindTexture(this.gl.TEXTURE_2D, texture0);
    this.gl.texParameteri(
      this.gl.TEXTURE_2D,
      this.gl.TEXTURE_WRAP_S,
      this.gl.CLAMP_TO_EDGE
    );
    this.gl.texParameteri(
      this.gl.TEXTURE_2D,
      this.gl.TEXTURE_WRAP_T,
      this.gl.CLAMP_TO_EDGE
    );
    this.gl.texParameteri(
      this.gl.TEXTURE_2D,
      this.gl.TEXTURE_MIN_FILTER,
      this.gl.LINEAR
    );
    this.gl.texParameteri(
      this.gl.TEXTURE_2D,
      this.gl.TEXTURE_MAG_FILTER,
      this.gl.LINEAR
    );
    this.gl.uniform1i(
      this.gl.getUniformLocation(this.gl.program, "samplerY"),
      0
    );

    let bufLen = this.NumCache[`${width}*${height}`];

    if (!bufLen) {
      bufLen = this.NumCache[`${width}*${height}`] = width * height;
    }

    var yBuffer = buffer.subarray(0, bufLen);
    this.gl.texImage2D(
      this.gl.TEXTURE_2D,
      0,
      this.gl.LUMINANCE,
      width,
      height,
      0,
      this.gl.LUMINANCE,
      this.gl.UNSIGNED_BYTE,
      yBuffer
    );

    var texture1 = this.gl.createTexture();
    this.gl.activeTexture(this.gl.TEXTURE1);
    this.gl.bindTexture(this.gl.TEXTURE_2D, texture1);
    this.gl.texParameteri(
      this.gl.TEXTURE_2D,
      this.gl.TEXTURE_WRAP_S,
      this.gl.CLAMP_TO_EDGE
    );
    this.gl.texParameteri(
      this.gl.TEXTURE_2D,
      this.gl.TEXTURE_WRAP_T,
      this.gl.CLAMP_TO_EDGE
    );
    this.gl.texParameteri(
      this.gl.TEXTURE_2D,
      this.gl.TEXTURE_MIN_FILTER,
      this.gl.LINEAR
    );
    this.gl.texParameteri(
      this.gl.TEXTURE_2D,
      this.gl.TEXTURE_MAG_FILTER,
      this.gl.LINEAR
    );
    this.gl.uniform1i(
      this.gl.getUniformLocation(this.gl.program, "samplerU"),
      1
    );

    let bufY = this.NumCache[`(${bufLen}*5)/4`];

    if (!bufY) {
      bufY = this.NumCache[`(${bufLen}*5)/4`] = (bufLen * 5) / 4;
    }

    let helfWidth = this.NumCache[`${width}/2`];

    if (!helfWidth) {
      helfWidth = this.NumCache[`${width}/2`] = width / 2;
    }

    let helfHeight = this.NumCache[`${height}/2`];

    if (!helfHeight) {
      helfHeight = this.NumCache[`${height}/2`] = height / 2;
    }

    this.gl.texImage2D(
      this.gl.TEXTURE_2D,
      0,
      this.gl.LUMINANCE,
      helfWidth,
      helfHeight,
      0,
      this.gl.LUMINANCE,
      this.gl.UNSIGNED_BYTE,
      buffer.subarray(bufLen, bufY)
    );

    var texture2 = this.gl.createTexture();
    this.gl.activeTexture(this.gl.TEXTURE2);
    this.gl.bindTexture(this.gl.TEXTURE_2D, texture2);
    this.gl.texParameteri(
      this.gl.TEXTURE_2D,
      this.gl.TEXTURE_WRAP_S,
      this.gl.CLAMP_TO_EDGE
    );
    this.gl.texParameteri(
      this.gl.TEXTURE_2D,
      this.gl.TEXTURE_WRAP_T,
      this.gl.CLAMP_TO_EDGE
    );
    this.gl.texParameteri(
      this.gl.TEXTURE_2D,
      this.gl.TEXTURE_MIN_FILTER,
      this.gl.LINEAR
    );
    this.gl.texParameteri(
      this.gl.TEXTURE_2D,
      this.gl.TEXTURE_MAG_FILTER,
      this.gl.LINEAR
    );
    this.gl.uniform1i(
      this.gl.getUniformLocation(this.gl.program, "samplerV"),
      2
    );

    let bufV = this.NumCache[`(${bufLen}*3)/2`];

    if (!bufV) {
      bufV = this.NumCache[`(${bufLen}*3)/2`] = (bufLen * 3) / 2;
    }

    this.gl.texImage2D(
      this.gl.TEXTURE_2D,
      0,
      this.gl.LUMINANCE,
      helfWidth,
      helfHeight,
      0,
      this.gl.LUMINANCE,
      this.gl.UNSIGNED_BYTE,
      buffer.subarray(bufY, bufV)
    );
  }

  draw(data, width, height, rotation) {
    // 指定清空<canvas>的颜色
    this.gl.clearColor(0.0, 0.0, 0.0, 1.0);

    // 清空<canvas>
    this.gl.clear(this.gl.COLOR_BUFFER_BIT);

    //根据rotation修改顶点
    let vertices = vertices0;
    if (rotation == 1) {
      vertices = vertices90;
    } else if (rotation == 2) {
      vertices = vertices180;
    } else if (rotation == 3) {
      vertices = vertices270;
    }

    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.vertexBuffer);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, vertices, this.gl.STATIC_DRAW);

    const a_Position = this.gl.getAttribLocation(this.gl.program, "a_Position");

    if (a_Position < 0) {
      console.log("Failed to get the storage location of a_Position");
      return -1;
    }
    this.gl.vertexAttribPointer(a_Position, 3, this.gl.FLOAT, false, 0, 0);
    this.gl.enableVertexAttribArray(a_Position);

    //填充纹理
    this.initTextureBuffer(data, width, height, rotation);
    this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);
  }
}

export { Render };
